'use client';

import Link from 'next/link';
import { Anchor, Paper, Stack, Text } from '@mantine/core';
import type { Locale } from '../../i18n/locales';
import { resolveHref } from '../../lib/content/localization';
import type { AutovakuutusSubpageLink } from '../../lib/content/autovakuutus';

type SidebarRootEntry = {
  slug: string;
  href: string;
  label: string;
};

const normalizeInternalPath = (value?: string) => {
  if (!value) return '';
  const noQuery = value.split('?')[0]?.split('#')[0] || '';
  let normalized = noQuery.trim();
  if (!normalized.startsWith('/')) normalized = `/${normalized}`;
  normalized = normalized.replace(/^\/(fi|sv|en)(?=\/|$)/, '');
  normalized = normalized.replace(/\/+$/, '');
  return normalized || '/';
};

export function AutovakuutusSidebar({
  locale,
  items,
  activeHref,
  title,
  rootEntry,
}: {
  locale: Locale;
  items: AutovakuutusSubpageLink[];
  activeHref?: string;
  title?: string;
  rootEntry?: SidebarRootEntry;
}) {
  if (!items.length) return null;

  const defaultRootEntry = {
    slug: 'autovakuutus',
    href: '/autovakuutus',
    label:
      locale === 'fi'
        ? 'Autovakuutus'
        : locale === 'sv'
          ? 'Bilförsäkring'
          : 'Car insurance',
  };

  const sidebarRoot = rootEntry || defaultRootEntry;
  const sidebarTitle =
    title ||
    (locale === 'fi' ? 'Autovakuutuksen aiheet' : locale === 'sv' ? 'Bilförsäkringens ämnen' : 'Car insurance topics');
  const childSectionLabel = locale === 'fi' ? 'Alasivut' : locale === 'sv' ? 'Undersidor' : 'Subpages';

  const renderEntry = (item: SidebarRootEntry | AutovakuutusSubpageLink) => {
    const current = normalizeInternalPath(activeHref || sidebarRoot.href);
    const target = normalizeInternalPath(item.href);
    const isActive = current === target;
    return (
      <Anchor
        key={item.slug}
        component={Link}
        href={resolveHref(locale, item.href)}
        underline="never"
        style={{
          borderRadius: 999,
          padding: '12px 14px',
          fontWeight: isActive ? 600 : 500,
          color: 'var(--text-primary)',
          border: isActive ? '1px solid var(--surface-border)' : '1px solid transparent',
          background: isActive ? 'var(--surface-soft)' : 'transparent',
        }}
      >
        {item.label}
      </Anchor>
    );
  };

  return (
    <Paper
      withBorder
      radius={40}
      p={24}
      component="aside"
      style={{
        background: 'var(--surface-base)',
        borderColor: 'var(--surface-border)',
      }}
    >
      <Stack gap="sm">
        <Text fw={600} size="lg" style={{ color: 'var(--text-primary)' }}>
          {sidebarTitle}
        </Text>
        {renderEntry(sidebarRoot)}
        <Text size="xs" fw={700} tt="uppercase" mt={8} style={{ color: 'var(--text-muted)', letterSpacing: '0.04em' }}>
          {childSectionLabel}
        </Text>
        {items.map(renderEntry)}
      </Stack>
    </Paper>
  );
}
